import { Component, Inject, OnInit, ViewChild } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { TokenStorageService } from 'src/app/core/auth/token-storage.service';
import { ApiCallService } from 'src/app/core/services/api-call-service';
import { SnackbarCollection } from 'src/app/core/services/snackbar.service';
import { ConfirmationDialogBoxComponent } from "src/app/shared/components/dialog-box";
import { TranslateDynamicText } from 'src/app/shared/pipes/dynamic-translation.pipe';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { create } from 'domain';
import { getdateformatService } from 'src/app/core/services/get-date-format.service';

@Component({
  selector: 'app-coupon-pool-create',
  templateUrl: './coupon-pool-create.component.html',
  styleUrls: ['./coupon-pool-create.component.css','../badge-create/badge-create.component.css']
})
export class CouponPoolCreateComponent implements OnInit {

  constructor(
      private dialogRefer: MatDialogRef<CouponPoolCreateComponent>,
      private dialog: MatDialog,
      private _apicall: ApiCallService,
      private _snackBar: SnackbarCollection,
      private tokenStorage: TokenStorageService,
      private _i18nDynamicTranslate: TranslateDynamicText,
      @Inject(MAT_DIALOG_DATA) public data: any
  ) { }

  @ViewChild('offerCreationSimplifiedComp') offerCreationSimplifiedComp;
  @ViewChild('giftCodeCreationComp') giftCodeCreationComp;

  ngOnInit(): void {
    if(this.data.mode.includes('edit')) {
      this.getCouponCreationViewData(this.data.couponPoolId,this.data.offerId,this.data.giftCodeId);
    }
  }

  closedialog(action) {
    if(action == 'refreshList') {
      this.dialogRefer.close({action:'refreshList'});
    } else {
      this.dialogRefer.close(false);
    }
  }

  poolConfig = {
    id: null,
    name: "",
    description  : "",
    codePrefix : "",
    codeSuffix: "",
    codeLength : null,
    codeCharacterSet : 'ALPHABETS',
    codeDisallowedCharacters : null,
    totalCoupons: null,
    isGiftCode: false,
  };

  codeCharacterSetSelectedValues = ['ALPHABETS'];
  updateCharacterSet(event) {
    this.errorFieldList = [];
    if (this.codeCharacterSetSelectedValues.length === 0) {
      const lastDeselected = event.value;
      this.codeCharacterSetSelectedValues = [lastDeselected === 'ALPHABETS' ? 'NUMBERS' : 'ALPHABETS'];
    }
  
    // Update the codeCharacterSet value
    this.poolConfig.codeCharacterSet = this.codeCharacterSetSelectedValues.join(',');
  }

  hasDisallowedCharacters = false;
  checkHasDisallowedCharacters(event,) {
    this.errorFieldList = [];
    if(event.checked) {
      this.hasDisallowedCharacters = true;
    } else {
      this.hasDisallowedCharacters = false;
    }
  }

  cleanTextInputs() {
    this.poolConfig.name = this.poolConfig.name?.trim();
    this.poolConfig.description = this.poolConfig.description?.trim();
    this.poolConfig.codePrefix = this.poolConfig.codePrefix?.trim();
    this.poolConfig.codeSuffix = this.poolConfig.codeSuffix?.trim();
    this.poolConfig.codeDisallowedCharacters = this.poolConfig.codeDisallowedCharacters?.trim();
  }

  scrollToErrorField() {
    setTimeout(() => {
      const errorField = document.querySelector('.error-text');
      if (errorField) {
          errorField.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 250);
  }

  errorFieldList = [];
  offerProperties = null;
  giftCodeProperties = null;
  isConfigValid() {
    this.cleanTextInputs();
    if(!this.poolConfig.name) {
      this.errorFieldList = [...this.errorFieldList,'NAME'];
    }
    if(!this.poolConfig.totalCoupons || this.poolConfig.totalCoupons > 3000000) {
      this.errorFieldList = [...this.errorFieldList,'TOTAL'];
    }
    if(!this.isUniqueCodeCapacityPossible()) {
      this.errorFieldList = [...this.errorFieldList,'CAPACITY'];
    }
    if(!this.poolConfig.codeLength || (this.poolConfig.codeLength < 3 || this.poolConfig.codeLength > 15)) {
      this.errorFieldList = [...this.errorFieldList,'CODELENGTH'];
    }
    if(this.errorFieldList.length>0) {
      this.scrollToErrorField();
      return false;
    }

    if(this.poolConfig.isGiftCode) {
      //fetch gift code properties from gift code creation component
      this.giftCodeProperties = this.giftCodeCreationComp.sendGiftCode();
      if(!this.giftCodeProperties) {
        this.scrollToErrorField();
        return false;
      }
      //remove the other property
      this.offerProperties = null;
    } else {
      //fetch offer properties from offer creation simplified component
      this.offerProperties = this.offerCreationSimplifiedComp.submitOfferForm('COUPONPOOL');
      if(!this.offerProperties || !this.offerProperties.hasOwnProperty('offerJson')) {
        this.scrollToErrorField();
        return false;
      }
      //remove the other property
      this.giftCodeProperties = null;
    }
    return true;
  }

  removeSpecialCharacters(key) {
    this.poolConfig[key] = this.poolConfig[key].replace(/[^a-zA-Z0-9]/g, '');
  }

  disableWheelScroll(event: WheelEvent): void {
    event.preventDefault();
  }
 
  isUniqueCodeCapacityPossible() {
     // check the max number of unique codes that can be generated with a buffer. Similar validation in backend
    const charPool = this.buildCharacterPool();
    const extraBuffer = 1.5;
    const maxCombinations = Math.pow(charPool.length, this.poolConfig.codeLength);
  
    // Ensure there is enough capacity in the character space to generate the requested number of unique codes.
		// A buffer multiplier (1.5x) is applied to account for the increasing likelihood of collisions when generating random codes.
		// If the total requested codes (with buffer) exceeds the maximum possible unique combinations, throw an error.
    if((Math.ceil(this.poolConfig.totalCoupons  * extraBuffer)) > maxCombinations) {
      return false; // Not enough unique codes possible
    }
    return true; // Enough unique codes possible
  }

  buildCharacterPool(): string[] {
    let pool: Set<string> = new Set();
    if (this.poolConfig.codeCharacterSet.includes('ALPHABETS')) {
      'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('').forEach(char => pool.add(char));
    }
    if (this.poolConfig.codeCharacterSet.includes('NUMBERS')) {
      '0123456789'.split('').forEach(char => pool.add(char));
    }
    if (this.poolConfig.codeDisallowedCharacters) {
      for (const c of this.poolConfig.codeDisallowedCharacters) {
        pool.delete(c);
      }
    }
    return Array.from(pool);
  }

  isSaving = false;
  saveConfig() {
    if(this.isConfigValid()) {
      let request = {
        ...this.poolConfig,
        offerRequestBean: this.offerProperties,
        giftCodeCreateRequest: this.giftCodeProperties,
      }
      if(this.data.mode == 'create' || this.data.mode == 'clone') {
        if(this.data.mode == 'clone') {
          delete this.poolConfig['id'];
        }
        this.isSaving = true;
        this._apicall.createRandomCodePool(request).subscribe(
          (response)=>{
            setTimeout(()=>{
              this.isSaving = false;
              this._snackBar.openSnackBar("Created Pool", 2000);
              this.closedialog('refreshList');
            },500);
          },
          (error)=>{
            this.isSaving = false;
            this._snackBar.openSnackBar("Error Creating pool : "+error.error, 4000);
            if(error.error.includes('same name already exists')) {
              this.errorFieldList = [...this.errorFieldList,'NAMECOPY'];
            }
          }
        );
      } else {
        if(request.offerRequestBean) {
          request.offerRequestBean.id = this.data.offerId;
        }
        if(request.giftCodeCreateRequest) {
          request.giftCodeCreateRequest.id = this.data.giftCodeId;
        }
        request['couponPoolId'] = this.data.couponPoolId;
        let message = `This will update all the coupons of the pool. Are you sure you want to update the pool properties?`
        if(this.data.mode == 'editCoupon') {
          request['couponId'] = this.data.couponId;
          message = `This will update the properties of this coupon. Are you sure you want to update the properties of this coupon?`;
        }
        const tempDialogRef = this.dialog.open(
          ConfirmationDialogBoxComponent,
          {
            panelClass: "no-padding-dialog-popup",
            width: "280px",
            data: {
              subject: `Save confirmation?`,
              message: message,
              cancelButtonText: this._i18nDynamicTranslate.transform("Cancel", ['POS_PAGE']),
              successButtonText: this._i18nDynamicTranslate.transform("Confirm", ['POS_PAGE']),
            }
          }
        );
        tempDialogRef.afterClosed().subscribe((result) => {
          if(result) {
            this.isSaving = true;
            this._apicall.updateCouponPoolProperties(request).subscribe(
              (response)=>{
                setTimeout(()=>{
                  this.isSaving = false;
                  this._snackBar.openSnackBar("Updating your properties..", 2000);
                  this.closedialog('refreshList');
                },500);
              },
              (error)=>{
                this.isSaving = false;
                this._snackBar.openSnackBar(error.error, 4000);
                if(error.error.includes('update redeemed coupon')) {
                  this.closedialog('refreshList');
                }
                console.log(error);
              }
            );
          }
        });
      }
    }
  }

  dataLoading = false;
  getCouponCreationViewData(couponPoolId,offerId,giftCodeId) {
    this.dataLoading = true;
    this._apicall.getCouponCreationViewData(couponPoolId,offerId,giftCodeId).subscribe(
      (response) => {
        //map response to poolConfig object
        response = response['body'];
        this.poolConfig.id = response['couponPoolEntity'].id;
        this.poolConfig.name = response['couponPoolEntity'].name;
        this.poolConfig.description = response['couponPoolEntity'].description;
        this.poolConfig.codePrefix = response['couponPoolEntity'].codePrefix;
        this.poolConfig.codeSuffix = response['couponPoolEntity'].codeSuffix;
        this.poolConfig.codeLength = response['couponPoolEntity'].codeLength;
        this.poolConfig.codeCharacterSet = response['couponPoolEntity'].codeCharacterSet;
        this.codeCharacterSetSelectedValues = this.poolConfig.codeCharacterSet.split(',');
        this.poolConfig.codeDisallowedCharacters = response['couponPoolEntity'].codeDisallowedCharacters;
        this.poolConfig.totalCoupons = response['couponPoolEntity'].totalCoupons;
        //offer properties
        this.offerProperties = JSON.parse(response['offerEntity']);
        this.giftCodeProperties = response['giftCodeEntity'];
        if(this.giftCodeProperties) {
          this.poolConfig.isGiftCode = true;
        }
        //if coupon property is edited then check name as coupon property will also have the same name
        if(this.data.mode == 'editCoupon' && !(this.data.hasSpecialConfig) && !this.data.has && this.offerProperties) {
          this.offerProperties['checkNameOnLoad'] = true;
        }
        if(this.data.mode == 'editCoupon' && !(this.data.hasSpecialConfig) && this.giftCodeProperties) {
          this.giftCodeProperties['checkNameOnLoad'] = true;
        }
        this.dataLoading = false;
      },
      (error) => {
        this._snackBar.openSnackBar("Error fetching coupon pool data : "+error.error, 4000);
        this.dataLoading = false;
      }
    );
  }

  numberInputHandler(event: KeyboardEvent,maxLength,inputType?) {

    const inputValue = (event.target as HTMLInputElement).value;
    const decimalPlaces = 2;
    if (inputType.toLowerCase() != 'float') {
      const invalidCharacters = ['e', '-', '+', '.'];
      if ((inputValue.length >= maxLength && event.key !== 'Backspace') || invalidCharacters.includes(event.key)) {
        event.preventDefault();
      }
    } else {
      const invalidCharacters = ['e', '-', '+']
      const dotIndex = inputValue.indexOf('.');
      if (dotIndex !== -1 && inputValue.substring(dotIndex + 1).length >= decimalPlaces && event.key !== 'Backspace') {
        event.preventDefault();
      } else {
        if ((inputValue.length >= maxLength && event.key !== 'Backspace') || invalidCharacters.includes(event.key)) {
          event.preventDefault();
        }
      }
    }
  }

}
